import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AccountAction } from '../types/customers.js';

export interface AccountStatusDialogProps {
  customerEmail: string;
  allowedActions: AccountAction[];
  onClose: () => void;
  onSubmit: (input: { action: AccountAction; reason: string }) => void;
  isSubmitting: boolean;
}

const ACTION_LABELS: Record<AccountAction, string> = {
  suspend: 'Suspend account',
  restore: 'Restore to active',
  lock: 'Lock account',
  unlock: 'Unlock account',
};

/** ADMIN-only. A reason is always required (Part 15) -- this dialog never offers `disabled`/`deleted` as a target, only the exact small allowlist (`suspend`/`restore`/`lock`/`unlock`) the backend itself enforces. */
export function AccountStatusDialog({
  customerEmail,
  allowedActions,
  onClose,
  onSubmit,
  isSubmitting,
}: AccountStatusDialogProps) {
  const [action, setAction] = useState<AccountAction | ''>(allowedActions[0] ?? '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const selectId = useId();
  const reasonId = useId();

  useEffect(() => {
    selectRef.current?.focus();
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function handleConfirm() {
    if (!action) {
      setError('Choose an action.');
      return;
    }
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    setError(null);
    onSubmit({ action, reason: reason.trim() });
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
      >
        <h2 className="text-base font-semibold text-gray-900">Account action — {customerEmail}</h2>

        {allowedActions.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">No account actions are available from this status.</p>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <label htmlFor={selectId} className="mb-1 block text-xs font-medium text-gray-600">
                Action
              </label>
              <select
                ref={selectRef}
                id={selectId}
                value={action}
                onChange={(event) => setAction(event.target.value as AccountAction)}
                className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
              >
                {allowedActions.map((option) => (
                  <option key={option} value={option}>
                    {ACTION_LABELS[option]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={reasonId} className="mb-1 block text-xs font-medium text-gray-600">
                Reason
              </label>
              <textarea
                id={reasonId}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={2}
                className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
              />
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-full border border-gray-200 px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
          {allowedActions.length > 0 && (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="rounded-full bg-red-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
            >
              {isSubmitting ? 'Applying…' : 'Apply'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
